
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { StrategyProvider } from "@/types/strategy-types";

interface ProviderFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (provider: Omit<StrategyProvider, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  initialData?: StrategyProvider;
}

export const ProviderFormDialog = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}: ProviderFormDialogProps) => {
  const [formData, setFormData] = useState<Omit<StrategyProvider, 'id' | 'created_at' | 'updated_at'>>({
    name: initialData?.name || "",
    url: initialData?.url || "",
    active: initialData?.active !== undefined ? initialData.active : true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogDescriptionId = "provider-form-description";

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md" aria-describedby={dialogDescriptionId}>
        <DialogHeader>
          <DialogTitle>{initialData ? "Edit" : "Create"} Strategy Provider</DialogTitle>
          <DialogDescription id={dialogDescriptionId}>
            {initialData
              ? "Update the strategy provider details."
              : "Add a new strategy provider."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Provider Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="url">Website URL</Label>
            <Input
              id="url"
              type="url"
              value={formData.url || ""}
              onChange={(e) => handleChange("url", e.target.value)}
              placeholder="https://example.com"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="active"
              checked={formData.active}
              onCheckedChange={(checked) => handleChange("active", !!checked)}
            />
            <Label htmlFor="active">Active</Label>
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {initialData ? "Updating..." : "Creating..."}
                </>
              ) : (
                initialData ? "Update" : "Create"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
